import React from 'react';
import { ClipboardCheck, AlertTriangle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useFM } from '@/context/FMContext';
import { useFMSop } from '@/hooks/useFMSop';
import { useTimeAdaptive } from '@/hooks/useTimeAdaptive';
import TimeAdaptiveSection from '@/components/fm/TimeAdaptiveSection';
import SwipeableTask from '@/components/fm/SwipeableTask';

/**
 * FMSopChecklist - Daily SOP checklist with time-adaptive sections.
 * Today tab: 3 time-slot sections with swipe-to-complete tasks.
 * Overdue tab: Carry-forward tasks with "Day X overdue" badges.
 * Summary bar shows completion progress at top.
 */
export default function FMSopChecklist() {
  const { selectedGymId } = useFM();
  const { tasks, overdue, summary, loading, error, refetch, completeTask, uncompleteTask } =
    useFMSop(selectedGymId);
  const currentTimeSlot = useTimeAdaptive();

  // Calculate completion percentage
  const completionPct = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;
  const progressColor =
    completionPct >= 80
      ? 'bg-green-500'
      : completionPct >= 50
      ? 'bg-yellow-500'
      : 'bg-red-500';
  const progressTextColor =
    completionPct >= 80
      ? 'text-green-700'
      : completionPct >= 50
      ? 'text-yellow-700'
      : 'text-red-700';

  // Sort overdue by overdue_days descending (most overdue first)
  const sortedOverdue = [...overdue].sort(
    (a, b) => (b.overdue_days || 0) - (a.overdue_days || 0)
  );

  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ClipboardCheck className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">Select a gym to view SOP checklist</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {/* Skeleton summary bar */}
        <div className="bg-white rounded-lg border p-4 animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
          <div className="h-2 bg-slate-200 rounded w-full" />
        </div>
        {/* Skeleton sections */}
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
            <div className="h-5 bg-slate-200 rounded w-2/5 mb-3" />
            <div className="h-3 bg-slate-100 rounded w-full mb-2" />
            <div className="h-3 bg-slate-100 rounded w-4/5" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700 text-sm">{error}</p>
        <button
          onClick={refetch}
          className="mt-2 text-sm text-red-600 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={refetch} pullingContent="" refreshingContent={
      <div className="text-center py-2 text-sm text-slate-500">Refreshing...</div>
    }>
      <div className="space-y-4">
        {/* Summary bar */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">
              {summary.completed}/{summary.total} tasks
            </span>
            <span className={`text-sm font-semibold ${progressTextColor}`}>
              {completionPct}% complete
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${progressColor} rounded-full transition-all duration-300`}
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>

        {/* Tabs: Today | Overdue */}
        <Tabs defaultValue="today">
          <TabsList className="w-full">
            <TabsTrigger value="today" className="flex-1">
              Today
            </TabsTrigger>
            <TabsTrigger value="overdue" className="flex-1">
              Overdue
              {overdue.length > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {overdue.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Today tab */}
          <TabsContent value="today">
            <div className="space-y-0 mt-3">
              <TimeAdaptiveSection
                timeSlot="morning_peak"
                currentTimeSlot={currentTimeSlot}
                tasks={tasks.morning_peak || []}
                onComplete={completeTask}
                onUncomplete={uncompleteTask}
              />
              <TimeAdaptiveSection
                timeSlot="off_peak"
                currentTimeSlot={currentTimeSlot}
                tasks={tasks.off_peak || []}
                onComplete={completeTask}
                onUncomplete={uncompleteTask}
              />
              <TimeAdaptiveSection
                timeSlot="evening_peak"
                currentTimeSlot={currentTimeSlot}
                tasks={tasks.evening_peak || []}
                onComplete={completeTask}
                onUncomplete={uncompleteTask}
              />
            </div>
          </TabsContent>

          {/* Overdue tab */}
          <TabsContent value="overdue">
            <div className="mt-3">
              {sortedOverdue.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardCheck className="w-10 h-10 text-green-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No overdue tasks</p>
                </div>
              ) : (
                <>
                  {/* Red banner */}
                  <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-700 font-medium">
                      {sortedOverdue.length} task{sortedOverdue.length !== 1 ? 's' : ''} overdue from previous days
                    </span>
                  </div>

                  {/* Overdue task list */}
                  <div className="bg-white rounded-lg border overflow-hidden">
                    {sortedOverdue.map((task) => (
                      <SwipeableTask
                        key={task.task_id}
                        task={task}
                        onComplete={completeTask}
                        onUncomplete={uncompleteTask}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PullToRefresh>
  );
}
